import styled from '@emotion/styled';
import React, { useState } from 'react';
import { FlowModal } from '../Modals/FlowModal/component';
import { FlowModalContent } from '../Modals/FlowModal/FlowModalContent/component';
import { FlowModalButton } from '../Modals/FlowModal/styles';
import { AllowCameraConfirmation } from './AllowCameraConfirmation/AllowCameraConfirmation';
import { WebcamRecorder } from './WebcamRecorder/WebcamRecorder';
import UserCheckGreen from './icons/user-check-green.svg';
import { AlertMessage } from '../AlertMessage/component';
import { FlowModalContentType } from '../Modals/FlowModal/types';
import { ROUTE_SSO } from '../../../ReduxStorage/routeActions';
import { HELP_ORIGIN } from '../../../constants';
import { RouteLink } from '../RouteLink/component';

const UserCheckImg = styled('img')({
  display: 'block',
  margin: '80px auto',
  width: 260,
  height: 260,
});

const FinishedRecordingWrapper = styled('div')({
  width: '100%',
});

enum FaceCaptureModalState {
  PERMISSION_REQUEST,
  RECORDING_PROCESS,
  FINISHED_RECORDING,
}

interface FaceCaptureModalProps {
  show: boolean;
  uploading: boolean;
  onFinishedRecording: (blob: Blob) => void;
  onCompleted: () => void;
}

export const FaceCaptureModal: React.FC<FaceCaptureModalProps> = ({
  show,
  uploading,
  onFinishedRecording,
  onCompleted,
}) => {
  const [modalState, setModalState] = useState<FaceCaptureModalState>(
    FaceCaptureModalState.PERMISSION_REQUEST
  );

  const flowModalContent = (modalState: FaceCaptureModalState): JSX.Element => {
    if (modalState === FaceCaptureModalState.RECORDING_PROCESS || uploading) {
      return (
        <FlowModalContent contentType={FlowModalContentType.Raw}>
          <WebcamRecorder
            uploading={uploading}
            onFinishedRecording={(blob) => {
              setModalState(FaceCaptureModalState.FINISHED_RECORDING);
              onFinishedRecording(blob);
            }}
          />
        </FlowModalContent>
      );
    }

    switch (modalState) {
      case FaceCaptureModalState.PERMISSION_REQUEST:
        return (
          <FlowModalContent
            contentType={FlowModalContentType.FullWidth}
            buttons={[
              <FlowModalButton
                primary
                label="Allow camera access"
                onClick={() =>
                  setModalState(FaceCaptureModalState.RECORDING_PROCESS)
                }
              />,
            ]}
          >
            <AllowCameraConfirmation />
          </FlowModalContent>
        );

      case FaceCaptureModalState.FINISHED_RECORDING:
        return (
          <FlowModalContent
            buttons={[
              <FlowModalButton label="OK" onClick={onCompleted} primary />,
            ]}
            contentType={FlowModalContentType.FullWidth}
          >
            <FinishedRecordingWrapper>
              <AlertMessage
                kind="success"
                title="Your facial identity has been recorded."
                message={
                  <RouteLink
                    to={{
                      type: ROUTE_SSO,
                      params: {
                        action: 'help',
                      },
                      query: {
                        url: `${HELP_ORIGIN}/t/35hl6l3`,
                      },
                    }}
                    target="_blank"
                  >
                    Learn more about identity verification on OpenLearning
                  </RouteLink>
                }
              />
              <UserCheckImg src={UserCheckGreen} alt="user-check" />
            </FinishedRecordingWrapper>
          </FlowModalContent>
        );
      default:
        return <></>;
    }
  };

  return (
    <>
      {show && (
        <FlowModal
          showClose={
            modalState === FaceCaptureModalState.FINISHED_RECORDING &&
            !uploading
          }
          title="Facial identity check"
          onHide={() => {
            if (
              modalState === FaceCaptureModalState.FINISHED_RECORDING &&
              !uploading
            ) {
              onCompleted();
            }
          }}
        >
          {flowModalContent(modalState)}
        </FlowModal>
      )}
    </>
  );
};
